import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { FiPlus, FiEdit, FiTrash2, FiTool } from "react-icons/fi";
import {
  SiPython,
  SiOpenjdk,
  SiCplusplus,
  SiC,
  SiJavascript,
  SiTypescript,
  SiReact,
  SiNodedotjs,
  SiMongodb,
  SiMysql,
  SiPostgresql,
  SiHtml5,
  SiCss3,
  SiPhp,
  SiGo,
  SiRust,
  SiGit,
  SiGithub,
  SiTailwindcss,
} from "react-icons/si";

import { skillsAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import Modal from "../components/Modal";

// Map of supported icons
const skillIcons = {
  python: <SiPython className="text-yellow-500 text-2xl" />,
  java: <SiOpenjdk className="text-red-600 text-2xl" />,
  cplusplus: <SiCplusplus className="text-blue-600 text-2xl" />,
  c: <SiC className="text-gray-600 text-2xl" />,
  javascript: <SiJavascript className="text-yellow-400 text-2xl" />,
  typescript: <SiTypescript className="text-blue-500 text-2xl" />,
  react: <SiReact className="text-cyan-500 text-2xl" />,
  node: <SiNodedotjs className="text-green-600 text-2xl" />,
  mongodb: <SiMongodb className="text-green-500 text-2xl" />,
  mysql: <SiMysql className="text-blue-500 text-2xl" />,
  postgresql: <SiPostgresql className="text-blue-700 text-2xl" />,
  html: <SiHtml5 className="text-orange-500 text-2xl" />,
  css: <SiCss3 className="text-blue-500 text-2xl" />,
  php: <SiPhp className="text-indigo-500 text-2xl" />,
  go: <SiGo className="text-sky-500 text-2xl" />,
  rust: <SiRust className="text-orange-700 text-2xl" />,
  git: <SiGit className="text-orange-600 text-2xl" />,
  github: <SiGithub className="text-gray-800 text-2xl" />,
  tailwindcss: <SiTailwindcss className="text-blue-500 text-2xl" />,
};

// Predefined icon options for dropdown
const iconOptions = Object.keys(skillIcons);

const Skills = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: "create",
    skill: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    skill: null,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm();

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    try {
      const response = await skillsAPI.getSkills();
      setSkills(response.data);
    } catch (error) {
      toast.error("Failed to fetch skills");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        name: data.name?.trim(),
        category: data.category,
        level: Number(data.level),
        icon: data.icon,
        order: data.order ? Number(data.order) : 0,
      };
      if (modalState.mode === "create") {
        await skillsAPI.createSkill(payload);
        toast.success("Skill added successfully");
      } else {
        await skillsAPI.updateSkill(modalState.skill._id, payload);
        toast.success("Skill updated successfully");
      }
      fetchSkills();
      closeModal();
    } catch (error) {
      toast.error(error.message || `Failed to ${modalState.mode} skill`);
    }
  };

  const handleEdit = (skill) => {
    setModalState({ isOpen: true, mode: "edit", skill });
    setValue("name", skill.name);
    setValue("category", skill.category);
    setValue("level", skill.level);
    setValue("icon", skill.icon || "");
    setValue("order", skill.order || 0);
  };

  const handleDelete = async (skillId) => {
    try {
      await skillsAPI.deleteSkill(skillId);
      setSkills(skills.filter((s) => s._id !== skillId));
      toast.success("Skill deleted successfully");
      setDeleteModal({ isOpen: false, skill: null });
    } catch (error) {
      toast.error("Failed to delete skill");
    }
  };

  const openCreateModal = () => {
    setModalState({ isOpen: true, mode: "create", skill: null });
    reset();
  };
  const closeModal = () => {
    setModalState({ isOpen: false, mode: "create", skill: null });
    reset();
  };

  const categories = ["frontend", "backend", "database", "tools", "other"];
  const categoryColors = {
    frontend: "bg-blue-100 text-blue-800",
    backend: "bg-green-100 text-green-800",
    database: "bg-purple-100 text-purple-800",
    tools: "bg-yellow-100 text-yellow-800",
    other: "bg-gray-100 text-gray-800",
  };
  const groupedSkills = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) acc[skill.category] = [];
    acc[skill.category].push(skill);
    return acc;
  }, {});

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Skills</h1>
          <p className="text-gray-600">
            Manage your technical skills and proficiency levels
          </p>
        </div>
        <button onClick={openCreateModal} className="btn btn-primary">
          <FiPlus className="mr-2" size={16} /> Add Skill
        </button>
      </div>

      {/* Skills by Category */}
      {categories.map(
        (cat) =>
          groupedSkills[cat] && (
            <div key={cat} className="card p-6">
              <div className="flex items-center mb-4">
                <h2 className="text-lg font-semibold capitalize text-gray-900">
                  {cat} Skills
                </h2>
                <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-sm">
                  {groupedSkills[cat].length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedSkills[cat]
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((skill) => (
                    <div key={skill._id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl">
                            {skillIcons[skill.icon?.toLowerCase()] || (
                              <FiTool className="text-gray-500 text-2xl" />
                            )}
                          </span>
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {skill.name}
                            </h3>
                            <span
                              className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                categoryColors[skill.category]
                              }`}
                            >
                              {skill.category}
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleEdit(skill)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                          >
                            <FiEdit size={14} />
                          </button>
                          <button
                            onClick={() =>
                              setDeleteModal({ isOpen: true, skill })
                            }
                            className="text-red-600 hover:text-red-800 p-1"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Proficiency</span>
                          <span className="font-medium">{skill.level}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${skill.level}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )
      )}

      {/* Empty State */}
      {skills.length === 0 && (
        <div className="text-center py-12">
          <FiTool className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No skills added yet
          </h3>
          <p className="text-gray-500 mb-4">
            Start building your skills showcase
          </p>
          <button onClick={openCreateModal} className="btn btn-primary">
            <FiPlus className="mr-2" size={16} /> Add Your First Skill
          </button>
        </div>
      )}

      {/* Skill Form Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.mode === "create" ? "Add New Skill" : "Edit Skill"}
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="form-label">Skill Name *</label>
            <input
              type="text"
              className={`form-input ${errors.name ? "border-red-500" : ""}`}
              placeholder="e.g., React, Node.js"
              {...register("name", { required: "Skill name is required" })}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>
          <div>
            <label className="form-label">Category *</label>
            <select
              className={`form-input ${
                errors.category ? "border-red-500" : ""
              }`}
              {...register("category", { required: "Category is required" })}
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat} className="capitalize">
                  {cat}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="text-red-500 text-sm mt-1">
                {errors.category.message}
              </p>
            )}
          </div>
          <div>
            <label className="form-label">Proficiency Level (1-100) *</label>
            <input
              type="number"
              min="1"
              max="100"
              className={`form-input ${errors.level ? "border-red-500" : ""}`}
              placeholder="80"
              {...register("level", {
                required: "Level is required",
                min: { value: 1, message: "Minimum 1" },
                max: { value: 100, message: "Maximum 100" },
              })}
            />
            {errors.level && (
              <p className="text-red-500 text-sm mt-1">
                {errors.level.message}
              </p>
            )}
          </div>
          <div>
            <label className="form-label">Icon *</label>
            <select
              className="form-input"
              {...register("icon", { required: "Icon is required" })}
            >
              <option value="">Select Icon</option>
              {iconOptions.map((ic) => (
                <option key={ic} value={ic}>
                  {ic}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Display Order</label>
            <input
              type="number"
              className="form-input"
              placeholder="0"
              {...register("order")}
            />
            <p className="text-gray-500 text-sm mt-1">
              Lower numbers appear first
            </p>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={closeModal}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {modalState.mode === "create" ? "Add Skill" : "Update Skill"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, skill: null })}
        title="Delete Skill"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete "{deleteModal.skill?.name}"? This
            action cannot be undone.
          </p>
          <div className="flex space-x-3 justify-end">
            <button
              onClick={() => setDeleteModal({ isOpen: false, skill: null })}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(deleteModal.skill._id)}
              className="btn btn-danger"
            >
              Delete Skill
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Skills;
